import moment from 'moment';

import * as dg from './dx/dataGridHelper';
import * as sys from './systemHelper';

export const isEndState = order => {
    return order && (order.isExpired || order.isDeactivated || ['Expired', 'Deactivated', 'Completed', 'Abandoned'].some(s => s === order.status));
}

export const canSendReorderReminder = (order) => {
    return order && order.status === 'Completed' &&
        order.purchase && order.purchase.total !== 0 &&
        order.items && order.items.length > 0 && order.items
            .map(i => { return i.productType })
            .some(type =>
                sys.configuration.workOrder.reorderReminderOptions.some(option => option.type === type)
            );
}

export const getStatusFilters = () => {
    return [
        {
            text: 'Incomplete',
            value: 'Incomplete',
        },
        {
            text: 'Ready to Submit',
            value: 'ReadyToSubmit',
        },
        {
            text: 'Submitted',
            value: 'Submitted',
        },
        {
            text: 'Waiting for Frame',
            value: 'WaitingForFrame',
        },
        {
            text: 'Partially Received',
            value: 'PartiallyReceived',
        },
        {
            text: 'Returned to Lab',
            value: 'ReturnedToLab',
        },
        {
            text: 'Waiting to Edge',
            value: 'WaitingToEdge',
        },
        {
            text: 'Ready',
            value: 'Ready'
        },
        {
            text: 'Customer Notified',
            value: 'CustomerNotified',
        },
        {
            text: 'Completed',
            value: 'Completed'
        },
        {
            text: 'On Hold',
            value: 'OnHold',
        },
        {
            text: 'Abandoned',
            value: 'Abandoned'
        },
    ];
}

export const getStatusDescription = (status) => {
    if (status) {
        switch (status) {
            case 'Incomplete':
                return 'Incomplete';

            case 'ReadyToSubmit':
                return 'Ready to submit';

            case 'Submitted':
                return 'Submitted';

            case 'WaitingForFrame':
                return 'Waiting for Frame';

            case 'PartiallyReceived':
                return 'Partially received';

            case 'ReturnedToLab':
                return 'Returned to Lab';

            case 'WaitingToEdge':
                return 'Waiting to Edge';

            case 'Ready':
                return 'Ready';

            case 'CustomerNotified':
                return 'Customer notified';

            case 'Completed':
                return 'Completed';

            case 'OnHold':
                return 'On hold';

            case 'Abandoned':
                return 'Abandoned';

            case 'Expired':
                return 'Expired';

            case 'Deactivated':
                return 'Deactivated';

            default:
                return 'Other';
        }
    }
}

export const getStatusTextColor = (status) => {
    if (status) {
        switch (status) {
            case 'Completed':
                return 'text-info-700';

            case 'CustomerNotified':
            case 'Ready':
                return 'text-success-600';

            case 'WaitingForFrame':
            case 'PartiallyReceived':
            case 'ReturnedToLab':
            case 'OnHold':
                return 'text-warning-700';

            case 'Submitted':
            case 'ReadyToSubmit':
            case 'WaitingToEdge':
                return 'text-gray-700';

            case 'Abandoned':
            case 'Expired':
            case 'Deactivated':
                return 'text-danger-700';

            case 'Incomplete':
                return 'text-gray-500';

            default:
                break;
        }
    }

    return '';
}

export const getWorkOrderDateFilters = () => {
    return dg.getDefaultLastDateFilters('WorkOrderDate');
}

export const getLastUpdatedDateFilters = () => {
    return dg.getDefaultLastDateFilters('LastUpdatedDateUtc');
}

export const getQuickFilterDescription = (type) => {
    if (type) {
        switch (type) {
            case 'ActiveWorkOrders':
                return `Active work orders`;

            case 'ReadyToSubmitWorkOrders':
                return `Ready to submit`;

            case 'ReadyWorkOrders':
                return `Ready for pick up`;

            case 'OnHoldWorkOrders':
                return `On hold`;

            case 'TodayWorkOrders':
                return `Today work orders`;

            case 'ThisWeekWorkOrders':
                return 'This week work orders';

            case 'LastWeekWorkOrders':
                return 'Last week work orders';

            case 'ThisMonthWorkOrders':
                return 'This month work orders';

            case 'LastMonthWorkOrders':
                return 'Last month work orders';

            case 'YtdWorkOrders':
                return 'Year-to-Date work orders';

            case 'LastYearWorkOrders':
                return `${moment().add(-1, 'years').format('YYYY')} work orders`;

            case 'RecentlyCreatedWorkOrders':
                return `Recently created work orders`;

            case 'RecentlySubmittedWorkOrders':
                return `Recently submitted work orders`;

            case 'RecentlyCompletedWorkOrders':
                return `Recently completed work orders`;

            case 'RecentlyAbandonedWorkOrders':
                return `Recently abandoned work orders`;

            default:
                return null;
        }
    }
    return null;
}

export const getQuickFilterDefinition = (type) => {
    if (type) {
        const statusFilters = getStatusFilters();
        const workOrderDateFilters = getWorkOrderDateFilters();
        const lastUpdatedDateFilters = getLastUpdatedDateFilters();

        switch (type) {
            case 'ActiveWorkOrders':
                return [{
                    column: 'WorkOrderDate',
                    sortOrder: 'asc'
                }, {
                    column: 'Status',
                    headerFilters: [
                        statusFilters.filter(u => u.text === 'Incomplete')[0].value,
                        statusFilters.filter(u => u.text === 'Ready to Submit')[0].value,
                        statusFilters.filter(u => u.text === 'Submitted')[0].value,
                        statusFilters.filter(u => u.text === 'Customer Notified')[0].value,
                        statusFilters.filter(u => u.text === 'Partially Received')[0].value,
                        statusFilters.filter(u => u.text === 'Waiting to Edge')[0].value,
                        statusFilters.filter(u => u.text === 'Ready')[0].value,
                        statusFilters.filter(u => u.text === 'On Hold')[0].value,
                    ],
                }]

            case 'ReadyToSubmitWorkOrders':
                return [{
                    column: 'LastUpdatedDateUtc',
                    sortOrder: 'asc'
                }, {
                    column: 'Status',
                    headerFilters: [
                        statusFilters.filter(u => u.text === 'Ready to Submit')[0].value,
                    ],
                }]

            case 'ReadyWorkOrders':
                return [{
                    column: 'WorkOrderDate',
                    sortOrder: 'asc'
                }, {
                    column: 'Status',
                    headerFilters: [
                        statusFilters.filter(u => u.text === 'Ready')[0].value,
                        statusFilters.filter(u => u.text === 'Customer Notified')[0].value,
                    ],
                }]

            case 'OnHoldWorkOrders':
                return [{
                    column: 'LastUpdatedDateUtc',
                    sortOrder: 'asc'
                }, {
                    column: 'Status',
                    headerFilters: [
                        statusFilters.filter(u => u.text === 'On Hold')[0].value,
                    ],
                }]

            case 'TodayWorkOrders':
                return [{
                    column: 'WorkOrderDate',
                    filter: {
                        operator: '=',
                        value: moment().startOf('day').toDate(),
                    },
                    sortOrder: 'asc'
                }]

            case 'ThisWeekWorkOrders':
                return [{
                    column: 'WorkOrderDate',
                    filter: {
                        operator: 'between',
                        value: [moment().startOf('week').startOf('day').toDate(), moment().endOf('week').startOf('day').toDate()],
                    },
                    sortOrder: 'asc'
                }]

            case 'LastWeekWorkOrders':
                return [{
                    column: 'WorkOrderDate',
                    filter: {
                        operator: 'between',
                        value: [moment().add(-1, 'weeks').startOf('week').startOf('day').toDate(), moment().add(-1, 'weeks').endOf('week').startOf('day').toDate()],
                    },
                    sortOrder: 'asc'
                }]

            case 'ThisMonthWorkOrders':
                return [{
                    column: 'WorkOrderDate',
                    filter: {
                        operator: 'between',
                        value: [moment().startOf('month').startOf('day').toDate(), moment().endOf('month').startOf('day').toDate()],
                    },
                    sortOrder: 'asc'
                }]

            case 'LastMonthWorkOrders':
                return [{
                    column: 'WorkOrderDate',
                    filter: {
                        operator: 'between',
                        value: [moment().add(-1, 'months').startOf('month').startOf('day').toDate(), moment().add(-1, 'months').endOf('month').startOf('day').toDate()],
                    },
                    sortOrder: 'asc'
                }]

            case 'YtdWorkOrders':
                return [{
                    column: 'WorkOrderDate',
                    filter: {
                        operator: 'between',
                        value: [moment().startOf('year').startOf('day').toDate(), moment().startOf('day').toDate()],
                    },
                    sortOrder: 'asc'
                }]

            case 'LastYearWorkOrders':
                return [{
                    column: 'WorkOrderDate',
                    filter: {
                        operator: 'between',
                        value: [moment().add(-1, 'years').startOf('year').startOf('day').toDate(), moment().add(-1, 'years').endOf('year').startOf('day').toDate()],
                    },
                    sortOrder: 'asc'
                }]

            case 'RecentlyCreatedWorkOrders':
                return [{
                    column: 'WorkOrderDate',
                    headerFilters: [workOrderDateFilters.filter(u => u.text === 'Last 30 days')[0].value],
                    sortOrder: 'desc'
                }]

            case 'RecentlySubmittedWorkOrders':
                return [{
                    column: 'LastUpdatedDateUtc',
                    headerFilters: [lastUpdatedDateFilters.filter(u => u.text === 'Last 30 days')[0].value],
                    sortOrder: 'desc'
                }, {
                    column: 'Status',
                    headerFilters: [
                        statusFilters.filter(u => u.text === 'Submitted')[0].value,
                    ],
                }]

            case 'RecentlyCompletedWorkOrders':
                return [{
                    column: 'LastUpdatedDateUtc',
                    headerFilters: [lastUpdatedDateFilters.filter(u => u.text === 'Last 30 days')[0].value],
                    sortOrder: 'desc'
                }, {
                    column: 'Status',
                    headerFilters: [
                        statusFilters.filter(u => u.text === 'Completed')[0].value,
                    ],
                }]

            case 'RecentlyAbandonedWorkOrders':
                return [{
                    column: 'LastUpdatedDateUtc',
                    headerFilters: [lastUpdatedDateFilters.filter(u => u.text === 'Last 30 days')[0].value],
                    sortOrder: 'desc'
                }, {
                    column: 'Status',
                    headerFilters: [
                        statusFilters.filter(u => u.text === 'Abandoned')[0].value,
                    ],
                }]

            default:
                return;
        }
    }
}